import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useModalQueue } from './ModalQueueContext';
import { useWelcomeOffer } from './WelcomeOfferContext';
import { apiClient } from '@/lib/api-client';
import { paymentStore } from '@/stores/paymentStore';
import { storage } from '@/lib/storage';

const shouldSkipGeoComply = (): boolean => {
  if (typeof window === 'undefined') return false;
  if (storage.getItem('SKIP_GEOCOMPLY') === 'true') return true;
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1';
};

interface ScheduledOffer {
  id: number;
  title: string;
  gc_amount: number;
  sc_amount: number;
  gc_bonus_amount: number;
  sc_bonus_amount: number;
  price: number;
  original_price: number | null;
  cadence_type: string;
  resets_at: string | null;
  can_purchase: boolean;
  purchases_remaining: number;
  group_id: number | null;
  is_welcome_offer: boolean;
}

interface ScheduledOfferGroup {
  id: number;
  title: string;
  offers: ScheduledOffer[];
  is_welcome_group: boolean;
}

interface MonthlyOfferContextType {
  showMonthlyOfferModal: boolean;
  setShowMonthlyOfferModal: (show: boolean) => void;
  currentMonthlyOffer: ScheduledOffer | null;
  currentMonthlyGroup: ScheduledOfferGroup | null;
  hasMoreMonthlyOffers: boolean;
  dismissCurrentOffer: () => void;
  checkAndShowMonthlyOffer: () => Promise<void>;
}

const MonthlyOfferContext = createContext<MonthlyOfferContextType | undefined>(undefined);

export const useMonthlyOffer = () => {
  const context = useContext(MonthlyOfferContext);
  if (context === undefined) {
    throw new Error('useMonthlyOffer must be used within a MonthlyOfferProvider');
  }
  return context;
};

const MONTHLY_OFFER_SHOWN_KEY = 'monthly_offer_shown_session';

export const MonthlyOfferProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, session, isGeoComplyCompliant } = useAuth();
  const { requestShow, notifyDismissed, shouldShow, hasHigherPriorityModal } = useModalQueue();
  const { showWelcomeOfferModal } = useWelcomeOffer();
  
  const [monthlyGroupsQueue, setMonthlyGroupsQueue] = useState<ScheduledOfferGroup[]>([]);
  const [monthlyOffersQueue, setMonthlyOffersQueue] = useState<ScheduledOffer[]>([]);
  
  const [currentMonthlyGroup, setCurrentMonthlyGroup] = useState<ScheduledOfferGroup | null>(null);
  const [currentMonthlyOffer, setCurrentMonthlyOffer] = useState<ScheduledOffer | null>(null);
  
  const [hasChecked, setHasChecked] = useState(false);
  const [isEligible, setIsEligible] = useState(false);
  const [isTemporarilyHidden, setIsTemporarilyHidden] = useState(false);
  
  const hasMoreMonthlyOffers = monthlyGroupsQueue.length > 0 || monthlyOffersQueue.length > 0;

  const loadNextOffer = useCallback(() => {
    if (monthlyGroupsQueue.length > 0) {
      const [nextGroup, ...remainingGroups] = monthlyGroupsQueue;
      setCurrentMonthlyGroup(nextGroup);
      setCurrentMonthlyOffer(null);
      setMonthlyGroupsQueue(remainingGroups);
      return true;
    }
    
    if (monthlyOffersQueue.length > 0) {
      const [nextOffer, ...remainingOffers] = monthlyOffersQueue;
      setCurrentMonthlyOffer(nextOffer);
      setCurrentMonthlyGroup(null);
      setMonthlyOffersQueue(remainingOffers);
      return true;
    }
    
    sessionStorage.setItem(MONTHLY_OFFER_SHOWN_KEY, 'true');
    setIsEligible(false);
    return false;
  }, [monthlyGroupsQueue, monthlyOffersQueue]);
  
  const dismissCurrentOffer = useCallback(() => {
    setIsTemporarilyHidden(false);
    setCurrentMonthlyGroup(null);
    setCurrentMonthlyOffer(null);
    notifyDismissed('MONTHLY_OFFER');
    
    setTimeout(() => {
      if (loadNextOffer()) {
        if (!paymentStore.getState().isOpen) {
          requestShow('MONTHLY_OFFER');
        }
      }
    }, 100);
  }, [notifyDismissed, loadNextOffer, requestShow]);
  
  useEffect(() => {
    let wasOpen = paymentStore.getState().isOpen;
    
    const unsubscribe = paymentStore.subscribe(() => {
      const isOpen = paymentStore.getState().isOpen;
      
      if (wasOpen && !isOpen && isEligible && (currentMonthlyOffer || currentMonthlyGroup)) {
        requestShow('MONTHLY_OFFER');
      }
      
      wasOpen = isOpen;
    });
    
    return unsubscribe;
  }, [isEligible, currentMonthlyOffer, currentMonthlyGroup, requestShow]);

  const checkAndShowMonthlyOffer = useCallback(async () => {
    if (!user || !session) return;
    if (!user.email_verified) return;

    const hasShownThisSession = sessionStorage.getItem(MONTHLY_OFFER_SHOWN_KEY);
    if (hasShownThisSession) return;

    try {
      const data = await apiClient.get('/coins/packages');
      
      if (!data || (!data.scheduledOffers && !data.success)) return;

      const scheduledOffers = data.scheduledOffers || { groups: [], ungrouped: [] };
      
      const monthlyUngrouped = (scheduledOffers.ungrouped || []).filter(
        (offer: ScheduledOffer) => 
          offer.cadence_type === 'monthly' && 
          !offer.is_welcome_offer && 
          offer.can_purchase !== false && 
          (offer.purchases_remaining ?? 1) > 0
      );
      
      const monthlyGrouped = (scheduledOffers.groups || []).filter(
        (group: ScheduledOfferGroup) => {
          const hasMonthlyOffer = group.offers.some(
            (o: ScheduledOffer) => o.cadence_type === 'monthly' && !o.is_welcome_offer
          );
          const hasPurchasableOffer = group.offers.some(
            (o: ScheduledOffer) => o.can_purchase !== false && (o.purchases_remaining ?? 1) > 0
          );
          return hasMonthlyOffer && hasPurchasableOffer && !group.is_welcome_group;
        }
      );

      if (monthlyUngrouped.length > 0 || monthlyGrouped.length > 0) {
        setIsEligible(true);
        
        if (monthlyGrouped.length > 0) {
          const [firstGroup, ...remainingGroups] = monthlyGrouped;
          setCurrentMonthlyGroup(firstGroup);
          setCurrentMonthlyOffer(null);
          setMonthlyGroupsQueue(remainingGroups);
          setMonthlyOffersQueue(monthlyUngrouped);
        } else if (monthlyUngrouped.length > 0) {
          const [firstOffer, ...remainingOffers] = monthlyUngrouped;
          setCurrentMonthlyOffer(firstOffer);
          setCurrentMonthlyGroup(null);
          setMonthlyOffersQueue(remainingOffers);
        }
        
        if (!paymentStore.getState().isOpen && !hasHigherPriorityModal('MONTHLY_OFFER')) {
          requestShow('MONTHLY_OFFER');
        }
      }
    } catch {
      // Silently fail
    }
  }, [user, session, requestShow, hasHigherPriorityModal]);

  useEffect(() => {
    const geoComplyPassed = shouldSkipGeoComply() || isGeoComplyCompliant === true;
    
    if (user && session && user.email_verified && geoComplyPassed && !hasChecked && !showWelcomeOfferModal) {
      checkAndShowMonthlyOffer();
      setHasChecked(true);
    } else if (!user) {
      setHasChecked(false);
      setIsEligible(false);
      setMonthlyGroupsQueue([]);
      setMonthlyOffersQueue([]);
      setCurrentMonthlyGroup(null);
      setCurrentMonthlyOffer(null);
      if (sessionStorage.getItem(MONTHLY_OFFER_SHOWN_KEY)) {
        sessionStorage.removeItem(MONTHLY_OFFER_SHOWN_KEY);
      }
    }
  }, [user, session, isGeoComplyCompliant, hasChecked, checkAndShowMonthlyOffer, showWelcomeOfferModal]);

  const queueShowsModal = shouldShow('MONTHLY_OFFER');
  const showMonthlyOfferModal = queueShowsModal && !isTemporarilyHidden;

  const setShowMonthlyOfferModal = useCallback((show: boolean) => {
    if (show) {
      setIsTemporarilyHidden(false);
    } else {
      setIsTemporarilyHidden(true);
    }
  }, []);

  const value = {
    showMonthlyOfferModal,
    setShowMonthlyOfferModal,
    currentMonthlyOffer,
    currentMonthlyGroup,
    hasMoreMonthlyOffers,
    dismissCurrentOffer,
    checkAndShowMonthlyOffer,
  };

  return (
    <MonthlyOfferContext.Provider value={value}>
      {children}
    </MonthlyOfferContext.Provider>
  );
};
